import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import get from 'lodash.get';
import { Typography } from '@kis-technologies/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { ProcessingOverlay } from 'components/ProcessingOverlay';
import { useActions } from 'hooks/useActions';
import { selectors as configSelectors, SearchOption } from 'services/configuration';
import { selectors as customerSelectors } from 'services/customer';
import { actions as hardwareActions } from 'services/hardware';
import { selectors as ticketingSelectors, actions as ticketingActions } from 'services/ticketing';
import { useStyles } from './styled';
import { MembershipLookupScreen } from './types';
import CustomSearch from './components/CustomSearch';
import BarcodeScan from './components/BarcodeScan';
import LookupSelection from './components/LookupSelection';

const MembershipLookupView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [screen, setScreen] = useState<MembershipLookupScreen | null>(null);
  const [lookupOption, setLookupOption] = useState<SearchOption | null>(null);
  const [value, setValue] = useState<any>(null);

  const [validateMembership, resetMembership, resetError, resetScanner] = useActions([
    ticketingActions.validateMembership,
    ticketingActions.resetMembership,
    ticketingActions.resetError,
    hardwareActions.resetScanner
  ]);
  const config = useSelector(configSelectors.selectConfiguration);
  const customer = useSelector(customerSelectors.selectCustomer);
  const membershipDetails = useSelector(ticketingSelectors.selectMembershipDetails);
  const ticketingError = useSelector(ticketingSelectors.selectError);
  const ticketingLoading = useSelector(ticketingSelectors.selectLoading);
  const navigationPath = useSelector(ticketingSelectors.selectNavigationPath);

  const support = get(config, `ticketing.supportName.${i18n.language}`, t('commonText.support'));
  const membershipLookup = get(config, 'ticketing.membershipLookup');
  const scanMembershipCardEnabled = get(membershipLookup, 'scanMembershipCard.enabled');
  const customSearchOptions: SearchOption[] = get(membershipLookup, 'customSearchOptions', []);
  const notFoundMessages = get(membershipLookup, 'notFoundMessages');
  const notFoundHeader = get(notFoundMessages, `header.${i18n.language}`, t('membershipSearch.membershipErrorTitle'));
  const notFoundSubheader = get(
    notFoundMessages,
    `subheader.${i18n.language}`,
    t('membershipSearch.membershipErrorDescription', { support })
  );
  const notFoundConfirmText = get(notFoundMessages, `buttons.${i18n.language}[0]`, t('commonText.tryAgain'));
  const notFoundCancelText = get(notFoundMessages, `buttons.${i18n.language}[1]`, t('commonText.back'));
  const loadingMessages = get(membershipLookup, `loadingMessages`);
  const loadingHeader = get(loadingMessages, `header.${i18n.language}`, t('membershipSearch.searchMembership'));
  const fontFamily = get(config, 'configuration.globalSettings.fonts.primary');

  const classes = useStyles({ fontFamily })();

  // determine what search options are enabled
  let enabledSearchOptions: SearchOption[] = [];
  if (customSearchOptions && customSearchOptions.length) {
    enabledSearchOptions = customSearchOptions.filter((option) => option.enabled);
  }
  if (scanMembershipCardEnabled) {
    enabledSearchOptions.push({ name: 'scanMembershipCard' });
  }

  // set the initial screen based on the config
  useEffect(() => {
    if (enabledSearchOptions.length === 1) {
      // skip tile selection if only one search option is enabled
      setLookupOption(enabledSearchOptions[0]);
      if (scanMembershipCardEnabled) {
        setScreen('barcode-scan');
      } else {
        setScreen('custom-collection');
      }
    } else {
      // tile selection
      setScreen('lookup-selection');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // nav forward if the lookup was successful
  useEffect(() => {
    if (membershipDetails.isLoggedIn) {
      const path = navigationPath === 'donations' ? '/ticketing/donation' : '/ticketing/genres';
      navigate(path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipDetails.isLoggedIn]);

  const resetState = () => {
    setValue(null);
    resetScanner();
    resetMembership();
    resetError();
  };

  const onCardSelect = (selectedOption: SearchOption) => {
    setLookupOption(selectedOption);
    if (selectedOption && selectedOption.name === 'scanMembershipCard') {
      setScreen('barcode-scan');
    } else {
      setScreen('custom-collection');
    }
  };

  const getScreen = () => {
    switch (screen) {
      case 'lookup-selection':
        return <LookupSelection onCardSelect={onCardSelect} onPrev={onPrevPageClick} />;
      case 'barcode-scan':
        return (
          <BarcodeScan
            onPrev={onPrevPageClick}
            onNext={setValue}
            lookupOption={lookupOption as SearchOption}
            loading={ticketingLoading}
            error={ticketingError}
          />
        );
      case 'custom-collection':
      default:
        return (
          <CustomSearch
            onPrev={onPrevPageClick}
            onNext={setValue}
            lookupOption={lookupOption as SearchOption}
            loading={ticketingLoading}
          />
        );
    }
  };

  const onPrevPageClick = () => {
    resetState();
    if ((screen === 'barcode-scan' || screen === 'custom-collection') && enabledSearchOptions.length > 1) {
      setScreen('lookup-selection');
    } else {
      // re-routes to the previous page (StartPurchaseView)
      navigate('/ticketing/start-purchase', { state: { from: location.pathname } });
    }
  };

  return (
    <>
      {ticketingError && (
        <ActionOverlay
          height={600}
          title={<Typography className={classes.memberNotFoundTitle}>{notFoundHeader}</Typography>}
          description={<Typography className={classes.memberNotFoundDescription}>{notFoundSubheader}</Typography>}
          confirmText={notFoundConfirmText}
          onConfirm={resetState}
          cancelText={notFoundCancelText}
          onCancel={onPrevPageClick}
        />
      )}
      {/* when a search value is submitted: display the processing overlay during the API call */}
      {value && (
        <ProcessingOverlay
          error={ticketingError}
          processed={customer ? true : false}
          processAction={() => validateMembership(value)}
          title={loadingHeader}
        />
      )}
      {/* set the current screen (selection, scan barcode, or custom data collection) */}
      {screen && getScreen()}
    </>
  );
};

export default MembershipLookupView;
